/* eslint-disable max-classes-per-file*/
import { Container } from 'aurelia-dependency-injection';
import { I18N } from 'aurelia-i18n';

import {
	Address,
	AddressFormatValueConverter,
} from '~/converters/address-format';
import {
	CodeAndDescriptionPairValueConverter,
	ColumnCodeToDescriptionValueConverter,
} from '~/converters/code-to-description';
import { CurrencyFormatValueConverter } from '~/converters/currency-format';
import { DateFormatValueConverter } from '~/converters/date-format';
import { ExistsNoneUnconfirmedConverter } from '~/converters/exists-none-unconfirmed';
import { NumberFormatValueConverter } from '~/converters/number-format';
import { PercentFormatValueConverter } from '~/converters/percent-format';
import { TfnValueConverter } from '~/converters/tfn';
import { ColumnValue } from '~/models/column-definition';
import { FieldModel } from '~/models/form-model';
import { Constant, constantsHolder } from '~/utils/constants';

@constantsHolder
class ConverterNames {
	public static readonly CONSTS = new ConverterNames();

	public readonly DATE_FORMAT = 'dateFormat';
	public readonly COLUMN_CODE_DESCRIPTION = 'columnCodeToDescription';
	public readonly CODE_DESCRIPTION = 'codeAndDescription';
	public readonly PERCENT_FORMAT = 'percentFormat';
	public readonly CURRENCY_FORMAT = 'currencyFormat';
	public readonly NUMBER_FORMAT = 'numberFormat';
	public readonly TFN = 'tfn';
	public readonly ADDRESS_FORMAT = 'addressFormat';
	public readonly EXISTS_NONE_UNCONFIRMED_FORMAT =
		'existsNoneUnconfirmedFormat';
}

export const ConverterTypes = ConverterNames.CONSTS;
export type ConverterType = Constant<ConverterNames>;

/**
 * Access aurelia converters from typescript
 */
// tslint:disable:max-classes-per-file
export class Converters {
	private static converters: { ConverterType?: any } = {};

	/**
	 * Pass value and arguments to the requested converter
	 *
	 * @param converter ConverterType of ValueConverter
	 * @param toConvert Value to convert
	 * @param args Arguments for converter function
	 */
	public static convertWith(
		converter: ConverterType,
		toConvert: any,
		...args
	) {
		return Converters.getConverter(converter).toView(toConvert, ...args);
	}

	public static getConverter(converter: ConverterType) {
		let converterInstance;

		if (typeof converter !== 'string') {
			throw new Error('Invalid converter type');
		}

		if (Converters.converters[converter]) {
			converterInstance = Converters.converters[converter];
		} else {
			switch (converter) {
				case ConverterTypes.DATE_FORMAT:
					converterInstance = new DateFormatValueConverter();
					break;
				case ConverterTypes.COLUMN_CODE_DESCRIPTION:
					converterInstance =
						new ColumnCodeToDescriptionValueConverter();
					break;
				case ConverterTypes.PERCENT_FORMAT:
					converterInstance = new PercentFormatValueConverter();
					break;
				case ConverterTypes.CURRENCY_FORMAT:
					converterInstance = new CurrencyFormatValueConverter();
					break;
				case ConverterTypes.NUMBER_FORMAT:
					converterInstance = new NumberFormatValueConverter();
					break;
				case ConverterTypes.TFN:
					converterInstance = new TfnValueConverter();
					break;
				case ConverterTypes.CODE_DESCRIPTION:
					converterInstance =
						new CodeAndDescriptionPairValueConverter();
					break;
				case ConverterTypes.ADDRESS_FORMAT:
					converterInstance = new AddressFormatValueConverter();
					break;
				case ConverterTypes.EXISTS_NONE_UNCONFIRMED_FORMAT:
					converterInstance = new ExistsNoneUnconfirmedConverter();
					break;
			}
			Converters.converters[converter] = converterInstance;
		}
		return converterInstance;
	}

	public static dateFormat(stringToConvert: string, format?: string) {
		return Converters.convertWith(
			ConverterTypes.DATE_FORMAT,
			stringToConvert,
			format,
		);
	}

	public static currencyFormat(stringToConvert: string, scale?: number) {
		return Converters.convertWith(
			ConverterTypes.CURRENCY_FORMAT,
			stringToConvert,
			scale,
		);
	}

	public static possiblyUnavailablePercentageFormat(
		fieldValue: number | null | undefined,
		scale: number,
	): string {
		if (fieldValue === null || fieldValue === undefined) {
			const i18n: I18N = Container.instance.get(I18N);
			return i18n.tr('special-field-values.unavailable');
		}
		return Converters.percentFormat(String(fieldValue), scale);
	}

	public static percentFormat(stringToConvert: string, scale: number) {
		return Converters.convertWith(
			ConverterTypes.PERCENT_FORMAT,
			stringToConvert,
			scale,
		);
	}

	public static numberFormat(stringToConvert: string, scale: number) {
		return Converters.convertWith(
			ConverterTypes.NUMBER_FORMAT,
			stringToConvert,
			scale,
		);
	}

	public static addressFormat(addressToConvert: Address) {
		return Converters.convertWith(
			ConverterTypes.ADDRESS_FORMAT,
			addressToConvert,
		);
	}

	public static existsNoneUnconfirmedFormat(stringToConvert: string) {
		return Converters.convertWith(
			ConverterTypes.EXISTS_NONE_UNCONFIRMED_FORMAT,
			stringToConvert,
		);
	}

	/**
	 * @param toConvert: value to convert
	 * @param args: parameters to be sent to ColumnCodeToDescriptionValueConverter.toView()
	 * @returns {string}
	 */
	public static columnCodeToDescription(
		toConvert: ColumnValue<string> | FieldModel,
		...args
	): string {
		return Converters.convertWith(
			ConverterTypes.COLUMN_CODE_DESCRIPTION,
			toConvert,
			...args,
		);
	}

	/**
	 * Format a field model based on its type, checking if has a code list.
	 * @param toConvert: FieldModel to display
	 * @returns {string}
	 */
	public static convertFieldModelBasedOnType(toConvert: FieldModel): string {
		if (toConvert.codeList != null && toConvert.codeList.length > 0) {
			return Converters.columnCodeToDescription(toConvert);
		}
		if (toConvert.dataType && toConvert.dataType.name === 'Date') {
			return Converters.convertWith(
				ConverterTypes.DATE_FORMAT,
				toConvert,
			);
		}
		return toConvert.value ? toConvert.value : toConvert.toString();
	}
}
